import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  userRole: string | null;
  profile: { display_name: string; avatar_url: string | null; email: string; cover_url?: string | null; country?: string | null; birthdate?: string | null; gender?: string | null } | null;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isAdmin: false,
  userRole: null,
  profile: null,
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [profile, setProfile] = useState<AuthContextType['profile']>(null);

  const fetchUserData = async (userId: string) => {
    try {
      const [{ data: roles }, { data: prof }, { data: adminCheck }] = await Promise.all([
        supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId),
        supabase
          .from('profiles')
          .select('display_name, avatar_url, email, cover_url, country, birthdate, gender')
          .eq('id', userId)
          .maybeSingle(),
        supabase.rpc('is_admin', { _user_id: userId }),
      ]);

      const topRole = roles && roles.length > 0
        ? roles.find(r => r.role === 'owner')?.role
          || roles.find(r => r.role === 'admin')?.role
          || roles.find(r => r.role === 'moderator')?.role
          || roles.find(r => r.role === 'editor')?.role
          || roles[0].role
        : null;

      setUserRole(topRole || (adminCheck ? 'admin' : null));
      setIsAdmin(Boolean(adminCheck) || topRole === 'owner' || topRole === 'admin');
      
      if (prof) {
        setProfile(prof);
        // Auto-detect country if not set
        if (!prof.country) {
          try {
            const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) });
            if (res.ok) {
              const geo = await res.json();
              if (geo.country_name) {
                await supabase.from('profiles').update({ country: geo.country_name } as any).eq('id', userId);
                setProfile(prev => prev ? { ...prev, country: geo.country_name } : prev);
              }
            }
          } catch { /* ignore geo errors */ }
        }
      } else {
        setProfile(null);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setIsAdmin(false);
      setUserRole(null);
      setProfile(null);
    }
  };

  const clearUserState = () => {
    setIsAdmin(false);
    setUserRole(null);
    setProfile(null);
  };

  useEffect(() => {
    let currentUserId: string | null = null;

    const syncSession = async (nextSession: Session | null, opts: { force?: boolean } = {}) => {
      const nextUserId = nextSession?.user?.id ?? null;
      const userChanged = nextUserId !== currentUserId;

      // Always keep session/user objects fresh (token rotation, etc.)
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      // Only show the loading screen + refetch profile/roles when the actual
      // signed-in user changes. This prevents the entire admin tree from
      // remounting on TOKEN_REFRESHED events that fire whenever the tab
      // regains focus (which was causing the admin panel to "refresh"
      // every time the user switched browser tabs).
      if (!userChanged && !opts.force) return;

      currentUserId = nextUserId;
      setLoading(true);

      if (nextSession?.user) {
        await fetchUserData(nextSession.user.id);
      } else {
        clearUserState();
      }

      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        void syncSession(nextSession);
      }
    );

    void supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      void syncSession(initialSession, { force: true });
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    clearUserState();
  };

  const refreshProfile = async () => {
    if (user) await fetchUserData(user.id);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, userRole, profile, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};
